import { BalanceData, ChestBand, OrderKind, OrderTemplate, Rarity } from "../data/balance";
import { ForgeStock } from "./forgeStock";
import { Fix64 } from "../maths/fix64";
import { Fnv1a64 } from "../maths/fnv1a64";
import { Pcg32 } from "../maths/pcg32";
import { ChestResult, rollChest } from "../sim/chest";
import { ownedTowers } from "../sim/ownership";
import { PlayerProfile } from "./profile";
import { goldFromLegacy, isLegacyResources } from "./gold";
import { Wallet } from "./wallet";
import { SupplyId, isSupplyId } from "../data/supplies";

/**
 * Daily Orders (ENGAGEMENT_2026-09-23 §B5, balance/src/orders.json): three small goals a UTC day, about half of them
 * lobby tasks and half counted from battles. Claiming pays gold and a speed-up; three claims in a day fill a
 * Supply chest and `weeklyGoal` claims in a UTC week a weekly chest. Unfinished orders carry over for `carryDays`, a
 * missed day costs nothing and there is no streak. Offline: the day comes from the client clock, like the play
 * reduction's daily cap (profile.ts), and every pick is deterministic from (playerId, day).
 */

export const SECONDS_PER_DAY = 86_400;
const DAYS_PER_WEEK = 7;
/** 1970-01-01 (day 0) was a Thursday: shifting by three days makes a UTC week run Monday to Sunday. */
const WEEK_START_SHIFT_DAYS = 3;
/** Pcg32 streams for the order picks and the Orders chests, apart from every other use of the same seeds. */
const PICK_STREAM = 211;
/** The `$tower` placeholder in a template's metric: one owned tower, picked when the order is issued. */
const TOWER_PARAM = "$tower";

export function utcDay(nowSeconds: number): number { return Math.floor(nowSeconds / SECONDS_PER_DAY); }
export function utcWeek(day: number): number { return Math.floor((day + WEEK_START_SHIFT_DAYS) / DAYS_PER_WEEK); }

/** Progress increments by key: `metric` or `metric:param` (orders.json `metric`, with `$tower` resolved). */
export type OrderCounts = Readonly<Record<string, number>>;

/** `supply` (MP-06): a pre-battle supply the order also pays (orders.json pool `supply`); absent: none. */
export interface OrderReward { readonly gold: number; readonly speedUpMinutes: number; readonly supply?: SupplyId }

export interface Order {
  readonly uid: number;
  /** Template id (orders.json `pool`). */
  readonly id: string;
  /** What counts towards it (`wins:frost` once `$tower` is resolved). */
  readonly key: string;
  /** The picked tower for a `$tower` order, else "". */
  readonly towerId: string;
  /** UTC day it was issued. */
  readonly day: number;
  readonly target: number;
  progress: number;
  claimed: boolean;
  /** Fixed when issued, at the player's level then. */
  readonly reward: OrderReward;
}

export const isComplete = (o: Order): boolean => o.progress >= o.target;

/** Expected gold in a chest of `band` at `level` (sim/chest.ts: level bundle × the band's mean rarity multiplier). */
export function averageChest(balance: BalanceData, level: number, band = ChestBand.Win): Fix64 {
  const c = balance.chests, weights = c.bandWeights[band];
  let total = 0;
  let mult = Fix64.Zero;
  weights.forEach((w, r) => { total += w; mult = mult.add(c.rarityMult[r].mulInt(w)); });
  return total === 0 ? Fix64.Zero : Fix64.fromInt(c.resourceBase + c.resourcePerLevel * level).mul(mult.divInt(total));
}

/** `fraction` of an average Win chest's gold at `level` (the "supplies" unit of Orders, Tribute, packs, events…). */
export function chestShare(balance: BalanceData, level: number, fraction: Fix64): number {
  return Math.max(0, averageChest(balance, level).mul(fraction).floorToInt());
}

/** One order's pay: its share of the day's `chestsPerDay` average Win chests, times its size, plus its speed-up. */
export function orderReward(balance: BalanceData, t: OrderTemplate, level: number): OrderReward {
  const o = balance.orders;
  const share = o.ordersPerDay > 0 ? o.chestsPerDay.divInt(o.ordersPerDay).mul(t.size) : Fix64.Zero;
  return { gold: chestShare(balance, Math.max(1, level), share), speedUpMinutes: t.speedUpMinutes, ...(t.supply ? { supply: t.supply } : {}) };
}

/** Seed for an Orders roll: FNV-1a over (playerId, what, n), like the offline level seeds (app/seeds.ts). */
export function metaSeed(playerId: string, what: string, n: number): bigint {
  const h = new Fnv1a64();
  h.writeString(playerId);
  h.writeString(what);
  h.writeInt(n);
  return h.value;
}

/** Pays an order (or any reward bundle) into the profile. */
export function grantReward(profile: PlayerProfile, r: OrderReward): void {
  profile.addGold(r.gold);
  profile.addSpeedUp(r.speedUpMinutes);
  if (r.supply) profile.supplies.add(profile.balance.commerce.shop.supplies, r.supply, 1);
}

export interface OrdersJson {
  orders: Order[];
  day: number; nextUid: number; seenDay: number;
  rerollDay: number; rerollsUsed: number;
  claimsDay: number; claimsToday: number; supplyFilledDay: number; suppliesReady: number;
  week: number; weekClaims: number; weeklyFilledWeek: number; weeklyReady: number;
  chestsOpened: number;
}

/** Seed tag and per-day stride of a VIP extra reroll's draw (apart from the free rerolls' seeds). */
const VIP_REROLL_TAG = "rerollVip";
const VIP_EXTRA_STRIDE = 64;

/** The player's Daily Orders: issue, carry over, progress, claim, reroll, and the Supply and weekly chests. */
export class DailyOrders {
  orders: Order[] = [];
  /** The UTC day orders were last issued (−1: never). */
  day = -1;
  private nextUid = 1;
  /** The day the player last opened the orders (the "new orders ready" line). */
  seenDay = -1;
  private rerollDay = -1;
  private rerollsUsed = 0;
  private claimsDay = -1;
  private claimsToday = 0;
  private supplyFilledDay = -1;
  suppliesReady = 0;
  private week = -1;
  private weekClaims = 0;
  private weeklyFilledWeek = -1;
  weeklyReady = 0;
  /** Orders chests opened so far: each one's seed index, so no two rolls repeat. */
  private chestsOpened = 0;
  /** VIP extra rerolls a day (docs/SHOP.md §2 `extraOrderRerolls`); set by @highmarch/app Deals, derived, not saved. */
  extraRerolls = 0;
  /** Where the weekly chest's gems go (D20); set by the app (LevelCatalog), not saved here. Null: no gems. */
  wallet: Wallet | null = null;
  /** The profile's forge, for the weekly chest's Forge Essence (gear v2); null pays none (a bare test fixture). */
  forge: ForgeStock | null = null;

  constructor(readonly balance: BalanceData, readonly playerId: string) {}

  unlocked(highestCleared: number): boolean { return this.balance.orders.pool.length > 0 && highestCleared >= this.balance.orders.unlockAfterLevel; }

  /**
   * Brings the orders to `now`: on a new day, drops what was claimed or ran out, keeps up to `maxCarried` unfinished
   * orders from the last `carryDays` days (completed ones always stay claimable) and issues the day's new ones. A clock
   * set backwards changes nothing.
   */
  refresh(now: number, highestCleared: number): void {
    if (!this.unlocked(highestCleared)) return;
    const today = utcDay(now), d = this.balance.orders;
    if (utcWeek(today) > this.week) { this.week = utcWeek(today); this.weekClaims = 0; }
    if (today <= this.day) return;
    const done = this.orders.filter((o) => !o.claimed && isComplete(o));
    const open = this.orders.filter((o) => !o.claimed && !isComplete(o) && o.day >= today - d.carryDays)
      .sort((a, b) => b.day - a.day || b.uid - a.uid).slice(0, d.maxCarried);
    this.orders = [...done, ...open].sort((a, b) => a.uid - b.uid);
    const rng = new Pcg32(metaSeed(this.playerId, "orders", today), PICK_STREAM);
    // The day's hub/battle split alternates (2+1, 1+2) so "about half" holds over any two days.
    const hub = today % 2 === 0 ? Math.ceil(d.ordersPerDay / 2) : Math.floor(d.ordersPerDay / 2);
    for (let i = 0; i < d.ordersPerDay; i++) {
      const o = this.draw(rng, i < hub ? "hub" : "battle", today, highestCleared) ?? this.draw(rng, i < hub ? "battle" : "hub", today, highestCleared);
      if (o) this.orders.push(o);
    }
    this.day = today;
  }

  /** An eligible template of `kind` not already active, issued for `day`; null when none is left. */
  private draw(rng: Pcg32, kind: OrderKind, day: number, highestCleared: number): Order | null {
    const active = this.orders.filter((o) => !o.claimed).map((o) => o.id);
    const owned = ownedTowers(this.balance, highestCleared);
    const towers = this.balance.towers.filter((t) => owned[t.index]);
    const pool = this.balance.orders.pool.filter((t) => t.kind === kind && t.minLevel <= highestCleared && !active.includes(t.id) && (!t.metric.includes(TOWER_PARAM) || towers.length > 0));
    if (pool.length === 0) return null;
    const t = pool[rng.nextInt(pool.length)];
    const towerId = t.metric.includes(TOWER_PARAM) ? towers[rng.nextInt(towers.length)].id : "";
    return { uid: this.nextUid++, id: t.id, key: t.metric.replace(TOWER_PARAM, towerId), towerId, day, target: t.target, progress: 0, claimed: false, reward: orderReward(this.balance, t, highestCleared) };
  }

  template(o: Order): OrderTemplate | undefined { return this.balance.orders.pool.find((t) => t.id === o.id); }

  /** Adds progress to every open order its key counts for. Returns the orders this completed. */
  record(counts: OrderCounts): Order[] {
    const finished: Order[] = [];
    for (const o of this.orders) {
      const n = counts[o.key] ?? 0;
      if (o.claimed || isComplete(o) || n <= 0) continue;
      o.progress = Math.min(o.target, o.progress + n);
      if (isComplete(o)) finished.push(o);
    }
    return finished;
  }

  /** Claims a completed order: returns its reward (the caller grants it) and counts it towards the Supply and weekly chests. */
  claim(uid: number, now: number): OrderReward | null {
    const o = this.orders.find((x) => x.uid === uid);
    if (!o || o.claimed || !isComplete(o)) return null;
    o.claimed = true;
    const today = utcDay(now), week = utcWeek(today), d = this.balance.orders;
    if (this.claimsDay !== today) { this.claimsDay = today; this.claimsToday = 0; }
    this.claimsToday++;
    if (this.claimsToday >= d.ordersPerDay && this.supplyFilledDay !== today) { this.supplyFilledDay = today; this.suppliesReady++; }
    if (this.week !== week) { this.week = week; this.weekClaims = 0; }
    this.weekClaims++;
    if (this.weekClaims >= d.weeklyGoal && this.weeklyFilledWeek !== week) { this.weeklyFilledWeek = week; this.weeklyReady++; }
    return o.reward;
  }

  rerollsLeft(now: number): number { return Math.max(0, this.balance.orders.freeRerollsPerDay + Math.max(0, this.extraRerolls) - (this.rerollDay === utcDay(now) ? this.rerollsUsed : 0)); }

  /** Swaps an unfinished order for another of the same kind (the day's free reroll). Returns the new order or why not. */
  reroll(uid: number, now: number, highestCleared: number): Order | string {
    const i = this.orders.findIndex((x) => x.uid === uid);
    if (i < 0) return "no such order";
    const o = this.orders[i];
    if (o.claimed || isComplete(o)) return "already complete";
    if (this.rerollsLeft(now) <= 0) return "no reroll left today";
    const today = utcDay(now);
    const used = this.rerollDay === today ? this.rerollsUsed : 0;
    const kind = this.template(o)?.kind ?? "battle";
    // The free rerolls keep their seeds; a VIP extra one draws from its own tag (the free index would reach tomorrow's).
    const free = this.balance.orders.freeRerollsPerDay;
    const seed = used < free ? metaSeed(this.playerId, "reroll", today * free + used) : metaSeed(this.playerId, VIP_REROLL_TAG, today * VIP_EXTRA_STRIDE + used - free);
    const rng = new Pcg32(seed, PICK_STREAM);
    const next = this.draw(rng, kind, today, highestCleared);
    if (!next) return "nothing else to offer";
    this.rerollDay = today;
    this.rerollsUsed = used + 1;
    this.orders[i] = next;
    return next;
  }

  /** Today's claims towards the Supply chest (full once it filled today). */
  supplyProgress(now: number): number {
    const today = utcDay(now), n = this.balance.orders.ordersPerDay;
    return this.supplyFilledDay === today ? n : this.claimsDay === today ? Math.min(n, this.claimsToday) : 0;
  }
  weeklyProgress(now: number): number {
    const week = utcWeek(utcDay(now));
    return this.weeklyFilledWeek === week ? this.balance.orders.weeklyGoal : this.week === week ? Math.min(this.balance.orders.weeklyGoal, this.weekClaims) : 0;
  }

  /** Opens a filled Supply chest: the existing roll with the supply band at the player's highest level. */
  openSupply(highestCleared: number): ChestResult | null {
    if (this.suppliesReady <= 0) return null;
    this.suppliesReady--;
    return rollChest(this.balance, Math.max(1, highestCleared), this.balance.orders.supplyBand, metaSeed(this.playerId, "supply", this.chestsOpened++));
  }
  /** Opens a filled weekly chest: the roll with the weekly band, plus `weeklyGems` into the wallet (D20) and `weeklyEssence` into the forge. */
  openWeekly(highestCleared: number): ChestResult | null {
    if (this.weeklyReady <= 0) return null;
    this.weeklyReady--;
    this.wallet?.grantGems(this.balance.orders.weeklyGems, "weeklyChest", this.day * SECONDS_PER_DAY, `week${this.week}`);
    if (this.forge) this.forge.essence += this.balance.orders.weeklyEssence;
    return rollChest(this.balance, Math.max(1, highestCleared), this.balance.orders.weeklyBand, metaSeed(this.playerId, "weekly", this.chestsOpened++));
  }

  /** Things the player can take now: completed orders plus filled chests (the Chests tab badge). */
  claimable(): number { return this.orders.filter((o) => !o.claimed && isComplete(o)).length + this.suppliesReady + this.weeklyReady; }

  /**
   * Today's orders the player has not looked at yet: the hook for the "while you were away" card. Call `refresh`
   * first; `markSeen` when the orders are on screen.
   */
  newOrders(now: number): Order[] {
    const today = utcDay(now);
    return this.seenDay >= today ? [] : this.orders.filter((o) => o.day === today && !o.claimed);
  }
  markSeen(now: number): void { this.seenDay = Math.max(this.seenDay, utcDay(now)); }

  toJson(): OrdersJson {
    return {
      orders: this.orders.map((o) => ({ ...o, reward: { ...o.reward } })), day: this.day, nextUid: this.nextUid, seenDay: this.seenDay,
      rerollDay: this.rerollDay, rerollsUsed: this.rerollsUsed, claimsDay: this.claimsDay, claimsToday: this.claimsToday,
      supplyFilledDay: this.supplyFilledDay, suppliesReady: this.suppliesReady, week: this.week, weekClaims: this.weekClaims,
      weeklyFilledWeek: this.weeklyFilledWeek, weeklyReady: this.weeklyReady, chestsOpened: this.chestsOpened,
    };
  }

  /** Restores from a save; missing or malformed fields keep their fresh defaults (an old save has none). */
  static fromJson(balance: BalanceData, playerId: string, j: unknown): DailyOrders {
    const o = new DailyOrders(balance, playerId);
    if (typeof j !== "object" || j === null) return o;
    const src = j as Partial<OrdersJson>;
    const target = o as unknown as Record<string, unknown>;
    for (const k of ["day", "nextUid", "seenDay", "rerollDay", "rerollsUsed", "claimsDay", "claimsToday", "supplyFilledDay", "suppliesReady", "week", "weekClaims", "weeklyFilledWeek", "weeklyReady", "chestsOpened"] as const) {
      if (Number.isInteger(src[k])) target[k] = src[k];
    }
    if (Array.isArray(src.orders)) o.orders = src.orders.filter(validOrder).map((x) => ({ ...x, reward: { gold: x.reward.gold, speedUpMinutes: x.reward.speedUpMinutes, ...(isSupplyId(x.reward.supply) ? { supply: x.reward.supply } : {}) } }));
    return o;
  }
}

function validOrder(x: unknown): x is Order {
  const o = x as Order;
  const r = o?.reward as OrderReward | undefined;
  return typeof o === "object" && o !== null && Number.isInteger(o.uid) && typeof o.id === "string" && typeof o.key === "string" && typeof o.towerId === "string"
    && Number.isInteger(o.day) && Number.isInteger(o.target) && Number.isInteger(o.progress) && typeof o.claimed === "boolean"
    && typeof r === "object" && r !== null && [r.gold, r.speedUpMinutes].every(Number.isInteger);
}

/** A chest of one fixed rarity (moved to sim/chest.ts in M36: a rare spawn's chest rolls one too). */
export { chestOfRarity } from "../sim/chest";
